import { useMemo } from 'react';

import {
  DatePicker as DatePickerBase,
  LocalizationProvider,
} from '@mui/x-date-pickers/';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { GlobalClasses } from 'helpers';
import { Langs, useTranslation } from 'libs/i18n';
import { GlobalStyles } from 'libs/ui/styled';

import { Icon } from '../Icon';
import { Input, InputProps } from '../Input';
import { useTheme } from '../theme';

import { DatePickerProps } from './types';

const CloseIcon = () => (
  <Icon.Close className={GlobalClasses.hovered} size={16} />
);

const CustomInput = ({ ...props }: any) => (
  // value приходит от MUI, если null — показываем пусто
  <Input {...props} value={props.value ?? ''} />
);
/**
 * DatePicker component.
 */
export const DatePicker = ({
  helperText,
  error,
  placeholder,
  name,
  showOptionalMark,
  showRequiredMark,
  fullWidth,
  disabled,
  readOnly,
  ...props
}: DatePickerProps) => {
  const { language, t } = useTranslation();

  const { theme } = useTheme();

  const adapterLocale = useMemo(() => {
    const locals: Record<Langs, typeof ru> = {
      [Langs.RU]: ru,
    };
    return locals[language];
  }, [language]);

  return (
    <LocalizationProvider
      adapterLocale={adapterLocale}
      dateAdapter={AdapterDateFns}
    >
      <GlobalStyles
        styles={({ palette }) => ({
          '.MuiPickersYear-root': {
            '.MuiPickersYear-yearButton': {
              textAlign: 'center',
              display: 'inline',
              '&.Mui-disabled': {
                color: palette.text.disabled,
              },
            },
          },
        })}
      />
      <DatePickerBase
        disableFuture
        disabled={disabled}
        disableOpenPicker={readOnly || disabled}
        localeText={{
          cancelButtonLabel: t('common.back'),
          toolbarTitle: t('components.datePicker.toolbarTitle'),
        }}
        readOnly={readOnly}
        slotProps={{
          openPickerButton: { disableRipple: true },
          field: { clearable: !disabled },
          textField: {
            helperText,
            error,
            placeholder,
            readOnly,
            name,
            showOptionalMark,
            showRequiredMark: showRequiredMark && !readOnly,
            fullWidth,
          } as InputProps,
          clearButton: { disableRipple: true, title: 'Clear' },
          openPickerIcon: { disableRipple: true },
        }}
        slots={{ textField: CustomInput, clearIcon: CloseIcon }}
        sx={{
          '& button.clearButton': {
            opacity: 1,
            padding: 0,
          },
          '& button': {
            ':hover': {
              color: theme.palette.hover,
            },
          },
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};
